import type { FC } from 'react';
import { useState } from 'react';
import { Button } from '@/components/atoms/Button';
import UnimoFullLogo from '@/assets/unimo-full-logo-standard.svg';
import { INDUSTRY_LINKS } from '@/shared/constants/navigation';

interface NavbarProps {
  onContactClick: () => void;
}

const Navbar: FC<NavbarProps> = ({ onContactClick }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const currentPath = window.location.pathname;

  const isIndustryPage = INDUSTRY_LINKS.some((link) => link.href === currentPath);

  return (
    <nav className="bg-white border-b border-gray-200" data-testid="main-nav">
      <div className="max-w-6xl mx-auto px-4">
        <div className="flex justify-between h-16">
          <a href="/" className="flex items-center gap-2" data-testid="nav-logo-section">
            <div className="relative h-11" data-testid="nav-logo">
              <img src={UnimoFullLogo} alt="unimo" className="h-full w-auto" />
              <div
                className="absolute inset-0 animate-sheen pointer-events-none"
                style={{
                  background: 'linear-gradient(110deg, transparent 30%, rgba(255,255,255,0.5) 50%, transparent 70%)',
                }}
              />
            </div>
            <span className="px-1.5 py-0.5 text-[10px] font-medium bg-gray-100 text-gray-500 rounded">
              BETA
            </span>
          </a>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8" data-testid="nav-links-desktop">
            <a href="/" className="text-gray-600 hover:text-gray-900 transition-colors">Home</a>

            {/* Industries Dropdown */}
            <div className="relative group">
              <button className={`${isIndustryPage ? 'text-gray-900 font-medium' : 'text-gray-600'} hover:text-gray-900 transition-colors flex items-center gap-1`}>
                Industries
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </button>
              <div className="absolute left-0 mt-2 w-48 bg-white border border-gray-200 rounded-lg shadow-lg opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all z-50">
                {INDUSTRY_LINKS.map((link, i) => {
                  const isActive = currentPath === link.href;
                  const isFirst = i === 0;
                  const isLast = i === INDUSTRY_LINKS.length - 1;
                  return (
                    <a
                      key={link.href}
                      href={link.href}
                      className={`block px-4 py-2 ${
                        isActive
                          ? 'text-gray-900 bg-gray-50 font-medium'
                          : 'text-gray-600 hover:bg-gray-50'
                      }${isFirst ? ' rounded-t-lg' : ''}${isLast ? ' rounded-b-lg' : ''}`}
                    >
                      {link.label}
                    </a>
                  );
                })}
              </div>
            </div>

            <Button
              onClick={onContactClick}
              className="bg-gray-900 text-white hover:bg-gray-800"
            >
              Contact Us
            </Button>
          </div>

          {/* Mobile Navigation Button */}
          <div className="md:hidden flex items-center">
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2 rounded-md text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              aria-expanded={isMobileMenuOpen}
              data-testid="mobile-menu-button"
            >
              <span className="sr-only">Open main menu</span>
              <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                {isMobileMenuOpen ? (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                ) : (
                  <path strokeLinecap="round" strokeLinejoin="round" d="M4 6h16M4 12h16M4 18h16" />
                )}
              </svg>
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Navigation Menu */}
      {isMobileMenuOpen && (
        <div className="md:hidden border-t border-gray-200" data-testid="mobile-menu">
          <div className="px-4 py-4 space-y-3">
            <a href="/" className="block py-2 text-gray-700 hover:text-gray-900">Home</a>
            <div className="py-2">
              <div className="text-sm font-medium text-gray-500 mb-2">Industries</div>
              {INDUSTRY_LINKS.map((link) => {
                const isActive = currentPath === link.href;
                return (
                  <a
                    key={link.href}
                    href={link.href}
                    className={`block py-1.5 pl-3 ${
                      isActive ? 'text-gray-900 font-medium' : 'text-gray-600 hover:text-gray-900'
                    }`}
                  >
                    {link.label}
                  </a>
                );
              })}
            </div>
            <Button
              onClick={() => { onContactClick(); setIsMobileMenuOpen(false); }}
              className="w-full bg-gray-900 text-white hover:bg-gray-800"
            >
              Contact Us
            </Button>
          </div>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
